import React, { useMemo, useState } from "react";
import { Circle, Pause, Play } from "lucide-react";
import { STAFF_PAUSE_REASONS, taskBoardColumnKey } from "../constants";
import { bucketForTask } from "../utils/taskQueue";
import { checklistProgress } from "../utils/taskChecklist";
import { TaskCardMeta } from "./TaskCardMeta";
import CompleteProofModal from "./CompleteProofModal";
import PauseReasonModal from "./PauseReasonModal";
import TaskLiveTimer from "./TaskLiveTimer";
import TaskQueueSwipe from "./TaskQueueSwipe";
import { isRecurringTask } from "../utils/taskReminderSync";

const SECTIONS = [
    { key: "now", label: "Now" },
    { key: "later", label: "Later" },
    { key: "waiting", label: "Waiting" },
];

const TaskStaffQueue = ({
    tasks = [],
    onView,
    onStart,
    onPause,
    onSnooze,
    onComplete,
}) => {
    const [pendingComplete, setPendingComplete] = useState(null);
    const [pendingPause, setPendingPause] = useState(null);

    const grouped = useMemo(() => {
        const map = { now: [], later: [], waiting: [] };
        tasks.forEach((task) => {
            const bucket = bucketForTask(task);
            if (bucket) map[bucket].push(task);
        });
        return map;
    }, [tasks]);

    const altFor = (col) => {
        if (col === "Paused") return { label: "Resume", run: (task) => onStart?.(task) };
        if (col === "In Progress") {
            return { label: "Snooze", run: (task) => onSnooze?.(task.id, 10) };
        }
        return { label: "Start", run: (task) => onStart?.(task) };
    };

    return (
        <div className="task-queue">
            <p className="task-queue__hint">
                Swipe right to complete. Swipe left to start, resume, or snooze 10 min.
            </p>
            {SECTIONS.map((section) => {
                const list = grouped[section.key] || [];
                return (
                    <section key={section.key} className="task-queue__section">
                        <header className="task-queue__head">
                            <h2>{section.label}</h2>
                            <span>{list.length}</span>
                        </header>
                        {list.length === 0 && (
                            <p className="task-queue__empty">Nothing here.</p>
                        )}
                        <div className="task-queue__list">
                            {list.map((task) => {
                                const col = taskBoardColumnKey(task);
                                const progress = checklistProgress(task);
                                const alt = altFor(col);
                                return (
                                    <article key={task.id} className="task-queue__card">
                                        <TaskQueueSwipe
                                            completeLabel="Complete"
                                            altLabel={alt.label}
                                            onComplete={() => setPendingComplete(task)}
                                            onAlt={() => alt.run(task)}
                                        >
                                            <button
                                                type="button"
                                                className="task-queue__body"
                                                onClick={() => onView?.(task)}
                                            >
                                                <TaskCardMeta task={task} isStaff person={null} />
                                                {progress.total > 0 && (
                                                    <span className="task-queue__check">
                                                        {progress.done}/{progress.total} steps
                                                    </span>
                                                )}
                                                <TaskLiveTimer timer={task.timer} />
                                            </button>
                                            <div className="task-queue__actions">
                                                {col !== "In Progress" && col !== "Paused" && (
                                                    <button
                                                        type="button"
                                                        onClick={() => onStart?.(task)}
                                                    >
                                                        <Play size={14} />
                                                        Start
                                                    </button>
                                                )}
                                                {col === "Paused" && (
                                                    <button
                                                        type="button"
                                                        onClick={() => onStart?.(task)}
                                                    >
                                                        <Circle size={14} />
                                                        Resume
                                                    </button>
                                                )}
                                                {col === "In Progress" && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setPendingPause(task)}
                                                    >
                                                        <Pause size={12} />
                                                        Pause
                                                    </button>
                                                )}
                                                <button type="button" onClick={() => onView?.(task)}>
                                                    Open
                                                </button>
                                                <button
                                                    type="button"
                                                    className="is-complete"
                                                    onClick={() => setPendingComplete(task)}
                                                >
                                                    Complete
                                                </button>
                                            </div>
                                        </TaskQueueSwipe>
                                    </article>
                                );
                            })}
                        </div>
                    </section>
                );
            })}

            <CompleteProofModal
                isOpen={Boolean(pendingComplete)}
                taskTitle={pendingComplete?.title}
                isRecurring={isRecurringTask(pendingComplete)}
                onClose={() => setPendingComplete(null)}
                onConfirm={(file) => {
                    if (!pendingComplete) return;
                    onComplete?.(pendingComplete, file || null);
                    setPendingComplete(null);
                }}
            />
            <PauseReasonModal
                isOpen={Boolean(pendingPause)}
                compact
                reasons={STAFF_PAUSE_REASONS}
                onClose={() => setPendingPause(null)}
                onConfirm={(reason) => {
                    if (!pendingPause) return;
                    onPause?.(pendingPause, reason);
                    setPendingPause(null);
                }}
            />
        </div>
    );
};

export default TaskStaffQueue;
